export function normalizeCode(value) {
  return (value ?? '').toLowerCase().replace(/[х×]/gu, 'x').replace(/,/gu, '.')
    .replace(/[\s\p{Z}]+/gu, '').replace(/[‐‑‒–—−]/gu, '-');
}

export function literalLength(query) {
  return normalizeCode(query).replace(/[?*\-]/gu, '').length;
}

export function isCodeQuery(value) {
  if (/^[0-9.]+(?:x[0-9.]+)+$/u.test(normalizeCode(value))) return false;
  return /^[a-z0-9*?.,/\-+="]+$/u.test(normalizeCode(value));
}

export function maskPattern(value) {
  let pattern = normalizeCode(value);
  if (pattern.replace(/\*+$/u, '').includes('*') && !pattern.startsWith('*')) pattern = '*' + pattern;
  return pattern.endsWith('*') ? pattern : pattern + '*';
}

// Dynamic programming avoids exponential regular-expression backtracking for masks.
function literalPositions(code, inputPattern) {
  const pattern = maskPattern(inputPattern);
  const reachable = Array.from({length: pattern.length + 1}, () =>
    new Uint8Array(code.length + 1));
  reachable[pattern.length][code.length] = 1;
  for (let i = pattern.length - 1; i >= 0; i--) {
    for (let j = code.length; j >= 0; j--) {
      reachable[i][j] = pattern[i] === '*'
        ? reachable[i + 1][j] || (j < code.length && reachable[i][j + 1])
        : j < code.length && (pattern[i] === '?' || pattern[i] === code[j])
          && reachable[i + 1][j + 1];
    }
  }
  if (!reachable[0][0]) return null;
  const positions = [];
  let i = 0;
  let j = 0;
  while (i < pattern.length) {
    if (pattern[i] === '*') {
      if (reachable[i + 1][j]) i++;
      else j++;
    } else {
      if (pattern[i] !== '?') positions.push(j);
      i++;
      j++;
    }
  }
  return positions;
}

export function highlightCode(value, query) {
  const source = value ?? '';
  if (!query || literalLength(query) < 2) return [{text: source, matched: false}];
  if (/[?*]/u.test(query)) return highlightMask(source, query, true);
  let normalized = '';
  const originalPositions = [];
  for (let index = 0; index < source.length; index++) {
    const character = normalizeCode(source[index]);
    normalized += character;
    for (let j = 0; j < character.length; j++) originalPositions.push(index);
  }
  const words = query.trim().split(/[\s\p{Z}]+/u);
  const candidates = new Set();
  if (isCodeQuery(query)) candidates.add(normalizeCode(query));
  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      const candidate = normalizeCode(words.slice(start, end).join(' '));
      if (!isCodeQuery(query) && literalLength(candidate) >= 2) candidates.add(candidate);
    }
  }
  const matched = new Uint8Array(source.length);
  for (const candidate of [...candidates].sort((a, b) => literalLength(b) - literalLength(a))) {
    const positions = literalPositions(normalized, candidate);
    if (!positions?.length) continue;
    for (let i = 0; i < positions.length; i++) {
      const original = originalPositions[positions[i]];
      matched[original] = 1;
      if (i > 0 && positions[i] === positions[i - 1] + 1) {
        for (let between = originalPositions[positions[i - 1]]; between < original; between++) {
          matched[between] = 1;
        }
      }
    }
    break;
  }
  const fragments = [];
  for (let i = 0; i < source.length; i++) {
    const isMatched = Boolean(matched[i]);
    const previous = fragments[fragments.length - 1];
    if (previous && previous.matched === isMatched) previous.text += source[i];
    else fragments.push({text: source[i], matched: isMatched});
  }
  return fragments.length ? fragments : [{text: source, matched: false}];
}

function normalizeTechnical(value) {
  return value.toLowerCase().replace(/[х×]/gu, 'x').replace(/,/gu, '.')
    .replace(/[\s\p{Z}]+/gu, '').replace(/[‐‑‒–—−]/gu, '-');
}

// Match the same complete title designations as headerMaskTokens in Elasticsearch.
function highlightMask(source, query, includeWholeCode = false) {
  const words = query.trim().split(/[\s\p{Z}]+/u);
  const patterns = new Set();
  if (isCodeQuery(query)) patterns.add(normalizeCode(query));
  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      const candidate = words.slice(start, end).join(' ');
      if (!isCodeQuery(query) && /[?*]/u.test(candidate) && literalLength(candidate) >= 2) {
        patterns.add(normalizeTechnical(candidate));
      }
    }
  }
  const targets = includeWholeCode ? [{text: source, offset: 0}] : [];
  for (const match of source.matchAll(/[\p{L}\p{N}]+(?:[.,/\-‐‑‒–—−]+[\p{L}\p{N}]+)*/gu)) {
    if (!(includeWholeCode && isCodeQuery(query)) && /\p{N}/u.test(match[0])) targets.push({text: match[0], offset: match.index});
  }
  const matched = new Uint8Array(source.length);
  for (const target of targets) {
    const normalized = normalizedPositions(target.text, normalizeTechnical);
    for (const pattern of patterns) {
      const positions = literalPositions(normalized.text, pattern);
      if (!positions) continue;
      for (const position of positions) matched[target.offset + normalized.positions[position]] = 1;
    }
  }
  return fragmentsFromMask(source, matched);
}

function parseNameQuery(query) {
  const codeIdentifiers = /(?<![\p{L}\p{N}])[a-z][a-z0-9]*(?:(?:(?:\s*[\-‐‑‒–—−\/])+\s*[a-z0-9*?.,]+)+|\s+[0-9*?][a-z0-9*?.,]*(?:(?:\s*[\-‐‑‒–—−\/])+\s*[a-z0-9*?.,]+)+)(?![\p{L}\p{N}])/giu;
  query = query.replace(codeIdentifiers, code => /[0-9*?]/u.test(code) ? ' '.repeat(code.length) : code);
  const number = '\\d+(?:\\s*[.,]\\s*\\d+)?';
  const units = '(?:мм|mm|см|cm|бар|bar|мпа|mpa|вт|w|кв|kv|в|v|шт|pcs|м|m)';
  const technical = new RegExp('(?<![\\p{L}\\p{N}])(?:'
    + number + '(?:\\s*[xх×/\\-]\\s*' + number + ')+(?:\\s*' + units + ')?'
    + '|(?:ø|диам\\.?|[dд])\\s*=?\\s*' + number
    + '|' + number + '\\s*' + units
    + '|[\\p{L}]*\\d[\\p{L}\\p{N}]*(?:[.,/\\-][\\p{L}\\p{N}]+)*'
    + ')(?![\\p{L}\\p{N}])', 'giu');
  const remaining = query.split('');
  const tokens = [];
  for (const match of query.matchAll(technical)) {
    tokens.push(normalizeTechnical(match[0]));
    remaining.fill(' ', match.index, match.index + match[0].length);
  }
  return {tokens, words: [...remaining.join('').matchAll(/\p{L}+/gu)].map(match => match[0].toLowerCase())};
}

function normalizedPositions(source, normalize = normalizeCode) {
  let text = '';
  const positions = [];
  for (let index = 0; index < source.length; index++) {
    const character = normalize(source[index]);
    text += character;
    for (let j = 0; j < character.length; j++) positions.push(index);
  }
  return {text, positions};
}

function fragmentsFromMask(source, mask) {
  const fragments = [];
  for (let i = 0; i < source.length; i++) {
    const matched = Boolean(mask[i]);
    const previous = fragments[fragments.length - 1];
    if (previous && previous.matched === matched) previous.text += source[i];
    else fragments.push({text: source[i], matched});
  }
  return fragments.length ? fragments : [{text: source, matched: false}];
}

function codeQuerySpan(code, query) {
  if (isCodeQuery(query)) {
    const pattern = normalizeCode(query);
    return literalPositions(normalizeCode(code), pattern)?.length
      ? {from: 0, to: query.length, pattern, masked: /[?*]/u.test(query)} : undefined;
  }
  const words = [...query.matchAll(/[^\s\p{Z}]+/gu)];
  const candidates = [];
  for (let start = 0; start < words.length; start++) {
    for (let end = start + 1; end <= words.length; end++) {
      const from = words[start].index;
      const to = words[end - 1].index + words[end - 1][0].length;
      const input = query.slice(from, to);
      const masked = /[?*]/u.test(input);
      const pattern = masked ? normalizeTechnical(input) : normalizeCode(input);
      if (literalLength(pattern) >= 2) candidates.push({from, to, pattern, masked});
    }
  }
  return candidates.sort((a, b) => literalLength(b.pattern) - literalLength(a.pattern))
    .find(candidate => literalPositions(candidate.masked ? normalizeTechnical(code) : normalizeCode(code), candidate.pattern)?.length);
}

function editDistance(left, right) {
  const distance = Array.from({length: left.length + 1}, () => new Uint16Array(right.length + 1));
  for (let i = 0; i <= left.length; i++) distance[i][0] = i;
  for (let j = 0; j <= right.length; j++) distance[0][j] = j;
  for (let i = 1; i <= left.length; i++) {
    for (let j = 1; j <= right.length; j++) {
      distance[i][j] = Math.min(distance[i - 1][j] + 1, distance[i][j - 1] + 1,
        distance[i - 1][j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && left[i - 1] === right[j - 2] && left[i - 2] === right[j - 1]) {
        distance[i][j] = Math.min(distance[i][j], distance[i - 2][j - 2] + 1);
      }
    }
  }
  return distance[left.length][right.length];
}

function sameWordForm(left, right) {
  if (!/^[а-яё]+$/u.test(left) || !/^[а-яё]+$/u.test(right)) return false;
  const ending = /(?:иями|ями|ами|ого|ему|ому|ыми|ими|ах|ях|ов|ев|ей|ам|ям|ом|ем|ой|ий|ый|ая|яя|ое|ее|ую|юю|ия|ие|ию|ии|ы|и|а|я|у|ю|о|е)$/u;
  const leftStem = left.replace(ending, '');
  return leftStem.length >= 3 && leftStem === right.replace(ending, '');
}

/** Highlight original title text; fuzzy comparisons never include the product's code. */
export function highlightName(value, query, productCode) {
  const source = value ?? '';
  if (!query || literalLength(query) < 2) return [{text: source, matched: false}];
  if (isCodeQuery(query) && !literalPositions(normalizeCode(productCode), normalizeCode(query))) {
    return [{text: source, matched: false}];
  }
  const normalized = normalizedPositions(source);
  const ownCode = normalizeCode(productCode);
  const matched = new Uint8Array(source.length);
  const protectedCode = new Uint8Array(source.length);
  let ownCodeFound = false;
  const mark = (from, to, mask = matched) => mask.fill(1, from, to);
  const codeSpan = ownCode ? codeQuerySpan(productCode, query) : undefined;
  if (ownCode) {
    let from = 0;
    let index;
    while ((index = normalized.text.indexOf(ownCode, from)) >= 0) {
      const start = normalized.positions[index];
      const end = normalized.positions[index + ownCode.length - 1] + 1;
      if ((start === 0 || !/[\p{L}\p{N}]/u.test(source[start - 1]))
          && (end === source.length || !/[\p{L}\p{N}]/u.test(source[end]))) {
        ownCodeFound = true;
        mark(start, end, protectedCode);
        let position = start;
        for (const fragment of highlightCode(source.slice(start, end), query)) {
          if (fragment.matched) mark(position, position + fragment.text.length);
          position += fragment.text.length;
        }
      }
      from = index + ownCode.length;
    }
  }
  // A title can contain only the queried code prefix, rather than the full productCode.
  if (!ownCodeFound && codeSpan) {
    const prefixSource = codeSpan.masked ? normalizedPositions(source, normalizeTechnical) : normalized;
    for (let start = 0; start < prefixSource.text.length; start++) {
      const originalStart = prefixSource.positions[start];
      if (originalStart > 0 && /[\p{L}\p{N}]/u.test(source[originalStart - 1])) continue;
      const positions = /[?*]/u.test(codeSpan.pattern)
        ? literalPositions(prefixSource.text.slice(start), codeSpan.pattern)
        : prefixSource.text.startsWith(codeSpan.pattern, start)
          ? Array.from({length: codeSpan.pattern.length}, (_, index) => index) : null;
      if (!positions?.length) continue;
      for (let i = 0; i < positions.length; i++) {
        const original = prefixSource.positions[start + positions[i]];
        mark(original, original + 1);
        mark(original, original + 1, protectedCode);
        if (i > 0 && positions[i] === positions[i - 1] + 1) {
          const previous = prefixSource.positions[start + positions[i - 1]];
          mark(previous, original);
          mark(previous, original, protectedCode);
        }
      }
    }
  }
  if (/[?*]/u.test(query) && !isCodeQuery(query)) {
    let position = 0;
    for (const fragment of highlightMask(source, query)) {
      if (fragment.matched) mark(position, position + fragment.text.length);
      position += fragment.text.length;
    }
  }
  const residualQuery = codeSpan
    ? query.slice(0, codeSpan.from) + ' ' + query.slice(codeSpan.to) : query;
  // A mask must not fall through into separate numeric literals or fuzzy words.
  const parsed = parseNameQuery(residualQuery.replace(/[^\s\p{Z}]*[?*][^\s\p{Z}]*/gu, ' '));
  const textWords = parsed.words;
  const textLength = textWords.join('').length;
  const errors = textLength < 3 ? 0 : textLength < 5 ? 1 : 2;

  // Technical literals never use fuzzy matching; hyphenated designations also match a literal prefix.
  const technicalSource = normalizedPositions(source, normalizeTechnical);
  for (const token of parsed.tokens) {
    let from = 0;
    let index;
    while ((index = technicalSource.text.indexOf(token, from)) >= 0) {
      const start = technicalSource.positions[index];
      const end = technicalSource.positions[index + token.length - 1] + 1;
      const before = source[start - 1] || '';
      const after = source[end] || '';
      const prefixValid = /^\p{L}/u.test(token) ? !/[\p{L}\p{N}]/u.test(before) : !/[\p{N}.,/]/u.test(before);
      const suffixValid = /\p{L}$/u.test(token) ? !/[\p{L}\p{N}]/u.test(after) : !/[\p{N}.,/xх×]/u.test(after);
      // A query such as 10-1 names the beginning of 10-1/4NPT, not a standalone number.
      // Keep the left boundary strict so it cannot highlight 110-1 or X10-1.
      const designationPrefix = token.includes('-')
        && !/[\p{L}\p{N}.,/\-‐‑‒–—−]/u.test(before);
      if ((prefixValid && suffixValid) || designationPrefix) mark(start, end);
      from = index + token.length;
    }
  }
  for (const group of source.matchAll(/[\p{L}\p{N}]+(?:[./,\-][\p{L}\p{N}]+)*/gu)) {
    if (/\p{N}/u.test(group[0])) continue;
    for (const word of group[0].matchAll(/\p{L}+/gu)) {
      const start = group.index + word.index;
      const end = start + word[0].length;
      if (protectedCode.subarray(start, end).some(Boolean)) continue;
      const actual = word[0].toLowerCase();
      for (const term of textWords) {
        if (actual.startsWith(term)) mark(start, start + term.length);
        else if (sameWordForm(actual, term)
            || (errors > 0 && Math.abs(actual.length - term.length) <= errors
                && editDistance(actual, term) <= errors)) mark(start, end);
      }
    }
  }
  return fragmentsFromMask(source, matched);
}
